import { FormEvent, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BellRing, CalendarClock, Loader2, X, XCircle } from 'lucide-react';
import { instructorApi } from '../api/instructor';
import { InstructorLesson } from '../types';

/** ISO (UTC) → valeur d'un <input type="datetime-local"> (heure locale). */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/**
 * Actions d'une leçon côté moniteur : la reporter (nouveaux horaires) ou
 * l'annuler. Masqué pour les créneaux ouverts et les leçons terminées/annulées.
 */
export default function LessonActions({ lesson }: { lesson: InstructorLesson }) {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [startsAt, setStartsAt] = useState(() => toLocalInput(lesson.startsAt));
  const [endsAt, setEndsAt] = useState(() => toLocalInput(lesson.endsAt));
  const [notify, setNotify] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['today'] });
    void queryClient.invalidateQueries({ queryKey: ['planning'] });
  };

  const cancelMutation = useMutation({
    mutationFn: () => instructorApi.cancelLesson(lesson.id),
    onSuccess: refresh,
    onError: (e: Error) => window.alert(e.message || 'Annulation impossible.'),
  });

  const rescheduleMutation = useMutation({
    mutationFn: () =>
      instructorApi.rescheduleLesson(lesson.id, {
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        notify,
      }),
    onSuccess: () => {
      refresh();
      setModalOpen(false);
    },
    onError: (e: Error) => setError(e.message || 'Report impossible.'),
  });

  const remindMutation = useMutation({
    mutationFn: () => instructorApi.remindLesson(lesson.id),
    onSuccess: () => window.alert("Rappel envoyé à l'élève."),
    onError: (e: Error) => window.alert(e.message || 'Rappel impossible.'),
  });

  if (
    lesson.isOpenSlot ||
    lesson.status === 'completed' ||
    lesson.status === 'cancelled'
  ) {
    return null;
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (new Date(endsAt).getTime() <= new Date(startsAt).getTime()) {
      setError('L’heure de fin doit être postérieure à l’heure de début.');
      return;
    }
    rescheduleMutation.mutate();
  };

  return (
    <>
      <button
        onClick={() => {
          setStartsAt(toLocalInput(lesson.startsAt));
          setEndsAt(toLocalInput(lesson.endsAt));
          setError(null);
          setModalOpen(true);
        }}
        className="px-3 py-2.5 border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-lg transition-colors flex items-center gap-2"
      >
        <CalendarClock size={15} />
        Reporter
      </button>
      <button
        onClick={() => remindMutation.mutate()}
        disabled={remindMutation.isPending}
        className="px-3 py-2.5 border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 disabled:opacity-60"
      >
        {remindMutation.isPending ? (
          <Loader2 size={15} className="animate-spin" />
        ) : (
          <BellRing size={15} className="text-indigo-600" />
        )}
        Rappel à l'élève
      </button>
      <button
        onClick={() => {
          if (window.confirm('Annuler cette leçon ? Le candidat sera prévenu.')) {
            cancelMutation.mutate();
          }
        }}
        disabled={cancelMutation.isPending}
        className="px-3 py-2.5 border border-red-200 text-red-600 hover:bg-red-50 text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 disabled:opacity-60"
      >
        {cancelMutation.isPending ? (
          <Loader2 size={15} className="animate-spin" />
        ) : (
          <XCircle size={15} />
        )}
        Annuler
      </button>

      {modalOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-end sm:items-center justify-center p-4"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="bg-white w-full sm:max-w-sm rounded-2xl p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">Reporter la leçon</h3>
              <button onClick={() => setModalOpen(false)} aria-label="Fermer">
                <X size={18} className="text-slate-400" />
              </button>
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Nouveau début
                </label>
                <input
                  type="datetime-local"
                  required
                  value={startsAt}
                  onChange={(e) => setStartsAt(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Nouvelle fin
                </label>
                <input
                  type="datetime-local"
                  required
                  value={endsAt}
                  onChange={(e) => setEndsAt(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 text-sm"
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                />
                Notifier l'élève du nouvel horaire
              </label>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={rescheduleMutation.isPending}
                className="w-full py-2.5 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-60"
              >
                {rescheduleMutation.isPending
                  ? 'Report en cours…'
                  : 'Confirmer le report'}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
